<template>
  <div class="approvalFlow">
    <el-collapse v-model="activeNames">
      <el-collapse-item name="flow">
        <template #title>
          <span>
            <img src="../../assets/svg/flowIcon.svg" alt="" />
            审批流程图
          </span>
        </template>
        <!-- 流程图内容 -->
        <div
          class="flow-container"
          ref="flowContainer"
          v-if="flowNodes.length > 0"
        >
          <!-- 右向树状布局 -->
          <div
            class="viewport-shell"
            ref="viewportShell"
            :class="{ panning: isPanning }"
            @wheel="handleWheel"
            @mousedown="handlePanStart"
          >
            <div class="flow-toolbar" @mousedown.stop>
              <el-button
                size="mini"
                icon="el-icon-zoom-in"
                circle
                @click="zoomIn"
              />
              <el-button
                size="mini"
                icon="el-icon-zoom-out"
                @click="zoomOut"
              />
              <el-button
                size="mini"
                icon="el-icon-refresh-left"
                @click="resetViewport"
              />
              <span class="zoom-text">{{ zoomPercent }}</span>
            </div>
            <div class="viewport-content" :style="viewportStyle">
              <div class="tree-wrapper" :style="wrapperStyle">
                <svg
                  class="connections-layer"
                  :width="svgWidth"
                  :height="svgHeight"
                  :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
                  overflow="visible"
                >
                  <defs>
                    <marker
                      id="arrowhead-completed-h"
                      markerWidth="6"
                      markerHeight="5"
                      refX="5"
                      refY="2.5"
                      orient="auto"
                    >
                      <polygon points="0 0, 6 2.5, 0 5" fill="#1890ff" />
                    </marker>
                    <marker
                      id="arrowhead-pending-h"
                      markerWidth="6"
                      markerHeight="5"
                      refX="5"
                      refY="2.5"
                      orient="auto"
                    >
                      <polygon points="0 0, 6 2.5, 0 5" fill="#d9d9d9" />
                    </marker>
                    <marker
                      id="arrowhead-reject-h"
                      markerWidth="6"
                      markerHeight="5"
                      refX="5"
                      refY="2.5"
                      orient="auto"
                    >
                      <polygon points="0 0, 6 2.5, 0 5" fill="#ff4d4f" />
                    </marker>
                  </defs>
                  <!-- 绘制所有连线 -->
                  <g v-for="(conn, index) in connections" :key="'conn-' + index">
                    <path
                      :d="conn.path"
                      fill="none"
                      :stroke="conn.color"
                      stroke-width="2"
                      :marker-end="conn.markerEnd"
                    />
                  </g>
                </svg>

                <!-- 渲染所有节点 -->
                <div
                  v-for="node in positionedNodes"
                  :key="node.nodeId"
                  class="tree-node"
                  :class="getNodeClass(node)"
                  :style="getNodeStyle(node)"
                >
                  <div class="node-avatar">
                    <img
                      v-if="getDisplayStatus(node) === 'completed'"
                      src="../../assets/img/process-icon-active.svg"
                      alt="已处理"
                    />
                    <img
                      v-else
                      src="../../assets/img/process-icon.svg"
                      alt="未处理"
                    />
                  </div>
                  <div class="node-info">
                    <div class="node-name" :title="formatNodeName(node)">
                      {{ formatNodeName(node) }}
                    </div>
                    <div class="node-action" :title="getNodeAction(node.nodeName)">
                      {{ getNodeAction(node.nodeName) }}
                    </div>
                  </div>
                </div>

                <svg
                  class="start-points-layer"
                  :width="svgWidth"
                  :height="svgHeight"
                  :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
                  overflow="visible"
                >
                  <g v-for="(conn, index) in connections" :key="'start-' + index">
                    <circle
                      v-if="conn.startCircle"
                      :cx="conn.startCircle.x"
                      :cy="conn.startCircle.y"
                      :r="conn.startCircle.radius"
                      :fill="conn.startCircle.fill"
                      :stroke="conn.startCircle.stroke"
                      :stroke-width="conn.startCircle.strokeWidth"
                    />
                  </g>
                </svg>

                <svg
                  class="labels-layer"
                  :width="svgWidth"
                  :height="svgHeight"
                  :viewBox="`0 0 ${svgWidth} ${svgHeight}`"
                  overflow="visible"
                >
                  <g v-for="(conn, index) in connections" :key="'label-' + index">
                    <text
                      v-if="conn.labelText"
                      class="edge-label"
                      :class="conn.labelClass"
                      :x="conn.labelX"
                      :y="conn.labelY"
                      text-anchor="middle"
                    >
                      {{ conn.labelText }}
                    </text>
                  </g>
                </svg>
              </div>
            </div>
          </div>
        </div>
        <!-- 无数据提示 -->
        <div v-else class="no-data">暂无流程数据</div>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
import { getFlowDiagram } from '@/api/feedbackKD';
import {
  buildFlowDepthInfo,
  buildSubProcessGroups,
  buildSubProcessSourceGroupMap,
  compareFlowNodes,
  getNodeGroupKey,
  normalizeFlowEdges,
  normalizeFlowNodes,
} from "./flow/kdFlowData";
import {
  getFlowNodeAnchor,
  getMainFlowEdgeRoute,
  getSubProcessEdgeRoute,
  getSubProcessLaunchRejectRoute,
  getSubProcessLaunchRoute,
  getSubProcessReturnRejectRoute,
  getSubProcessReturnRoute,
  getSubProcessReturnSourceAnchor,
} from "./flow/kdFlowRouting";

export default {
  name: "approvalFlow",
  props: {
    questionId: {
      type: String,
      default: "",
    },
    /**
     * 节点宽度
     */
    nodeWidth: {
      type: Number,
      default: 130,
    },
    /**
     * 节点高度
     */
    nodeHeight: {
      type: Number,
      default: 70,
    },
    /**
     * 层级间距（水平方向）
     */
    levelGap: {
      type: Number,
      default: 80,
    },
    /**
     * 节点间距（垂直方向）
     */
    nodeGap: {
      type: Number,
      default: 34,
    },
  },
  data() {
    return {
      activeNames: ["flow"],
      flowNodes: [],
      flowEdges: [],
      scale: 1,
      translateX: 0,
      translateY: 0,
      isPanning: false,
      panStartX: 0,
      panStartY: 0,
      panOriginX: 0,
      panOriginY: 0,
    };
  },
  computed: {
    /**
     * 节点映射 nodeId -> node
     */
    nodeMap() {
      const map = {};
      this.flowNodes.forEach((node) => {
        map[node.nodeId] = node;
      });
      return map;
    },

    /**
     * 可渲染边
     */
    validEdges() {
      return (this.flowEdges || []).filter((edge) => {
        return this.nodeMap[edge.from] && this.nodeMap[edge.to];
      });
    },

    /**
     * 进入子流程的桥接边
     */
    enterBridgeEdges() {
      return this.validEdges.filter((edge) => this.isEnterBridgeEdge(edge));
    },

    /**
     * 参与深度计算的边
     */
    depthEdges() {
      return this.validEdges.filter((edge) => {
        return edge.edgeType === "forward" || this.isEnterBridgeEdge(edge);
      });
    },

    /**
     * 顶部回边
     */
    topLoopEdges() {
      return this.validEdges.filter((edge) => {
        return edge.edgeType === "reject" && !this.getCounterpartEdge(edge);
      });
    },

    /**
     * 底部回桥边
     */
    bottomLoopEdges() {
      return this.validEdges.filter((edge) => this.isReturnBridgeEdge(edge));
    },

    /**
     * 使用自定义深度算法计算布局
     */
    treeLayout() {
      if (this.flowNodes.length === 0) {
        return {
          nodePositions: {},
          width: 0,
          height: 0,
          nodeDepths: {},
          columns: {},
          topLoopEdges: this.topLoopEdges,
          bottomLoopEdges: this.bottomLoopEdges,
          topLoopMargin: 0,
          bottomLoopMargin: 0,
        };
      }

      const columnWidth = this.nodeWidth + this.levelGap;
      const rowHeight = this.nodeHeight + this.nodeGap;
      const leftPadding = 40;
      const topPadding = 40;
      const mainNodes = this.flowNodes.filter(
        (node) => node.processType !== "sub"
      );
      const mainNodeIds = new Set(mainNodes.map((node) => node.nodeId));
      const mainDepthEdges = this.validEdges.filter((edge) => {
        return (
          edge.edgeType === "forward" &&
          mainNodeIds.has(edge.from) &&
          mainNodeIds.has(edge.to)
        );
      });
      const mainDepthInfo = this.buildDepthInfo(mainNodes, mainDepthEdges);
      const longestRemainingLengthMap = this.buildLongestRemainingLengthMap(
        mainNodes,
        mainDepthEdges
      );
      const rowIndexMap = this.buildMainRowIndexMap(
        mainNodes,
        mainDepthEdges,
        longestRemainingLengthMap
      );
      this.compressFourNodeBridgeGapRow(
        mainDepthEdges,
        rowIndexMap,
        mainDepthInfo.nodeDepths
      );
      const orderedColumns = this.optimizeColumns(
        mainDepthInfo.columns,
        rowIndexMap
      );
      const subProcessGroups = this.getSubProcessGroups();
      const groupHeights = {};
      subProcessGroups.forEach((group) => {
        groupHeights[group.businessKey] = this.calculateSubGroupHeight(group);
      });
      const sourceGroupMap = this.buildSourceGroupMap(subProcessGroups);
      const subProcessSourceDepthSet = new Set(
        mainNodes
          .filter((node) => (sourceGroupMap[node.nodeId] || []).length > 0)
          .map((node) => mainDepthInfo.nodeDepths[node.nodeId] || 0)
      );
      const sortedSubProcessSourceDepths = Array.from(subProcessSourceDepthSet).sort(
        (left, right) => left - right
      );
      const nodePositions = {};
      const rowNodesMap = {};
      mainNodes.forEach((node) => {
        const rowIndex = rowIndexMap[node.nodeId] ?? 0;
        if (!rowNodesMap[rowIndex]) {
          rowNodesMap[rowIndex] = [];
        }
        rowNodesMap[rowIndex].push(node);
      });
      const maxMainRows = Math.max(
        1,
        ...Object.values(rowIndexMap).map((rowIndex) => rowIndex + 1)
      );
      const rowCenters = [];
      let currentRowTop = topPadding;

      for (let rowIndex = 0; rowIndex < maxMainRows; rowIndex++) {
        let rowReservedTop = 0;
        (rowNodesMap[rowIndex] || []).forEach((node) => {
          const groups = sourceGroupMap[node.nodeId] || [];
          if (groups.length === 0) {
            return;
          }
          const subAreaHeight = groups.reduce((total, group, groupIndex) => {
            const groupHeight =
              groupHeights[group.businessKey] || this.nodeHeight;
            return total + groupHeight + (groupIndex > 0 ? 30 : 0);
          }, 0);
          rowReservedTop = Math.max(rowReservedTop, 45 + subAreaHeight);
        });
        rowCenters[rowIndex] =
          currentRowTop + rowReservedTop + this.nodeHeight / 2;
        currentRowTop += rowReservedTop + this.nodeHeight + this.nodeGap;
      }

      Object.keys(orderedColumns).forEach((depthKey) => {
        const depth = Number(depthKey);
        const columnNodes = orderedColumns[depth];
        columnNodes.forEach((node) => {
          const hasSubProcessGroups =
            (sourceGroupMap[node.nodeId] || []).length > 0;
          const nodeRowIndex = rowIndexMap[node.nodeId] ?? 0;
          /**
           * 子流程发起节点会占用右侧一列。
           * 这里按深度累加前序发起列偏移，避免后续主流程节点与发起节点重叠。
           */
          const cascadedDepthOffset = sortedSubProcessSourceDepths.reduce(
            (offset, sourceDepth) => {
              return sourceDepth < depth ? offset + 1 : offset;
            },
            0
          );
          nodePositions[node.nodeId] = {
            x:
              leftPadding +
              (depth + cascadedDepthOffset + (hasSubProcessGroups ? 1 : 0)) *
                columnWidth +
              this.nodeWidth / 2,
            y: rowCenters[nodeRowIndex],
          };
        });
      });

      const mainMaxColumnDepth = Math.max(
        0,
        ...Object.keys(orderedColumns).map((depth) => Number(depth))
      );
      const mainLayoutHeight = Math.max(
        this.nodeHeight,
        currentRowTop - topPadding
      );
      const nodeDepths = { ...mainDepthInfo.nodeDepths };
      subProcessGroups.forEach((group) => {
        const sourcePosition = nodePositions[group.sourceNodeId];
        const sourceDepth = nodeDepths[group.sourceNodeId] || 0;
        const sourceGroups = sourceGroupMap[group.sourceNodeId] || [];
        const groupIndex = sourceGroups.findIndex((item) => {
          return item.businessKey === group.businessKey;
        });
        const groupsBeforeHeight = sourceGroups
          .slice(0, groupIndex)
          .reduce((total, item, index) => {
            return (
              total +
              (groupHeights[item.businessKey] || this.nodeHeight) +
              (index >= 0 ? 30 : 0)
            );
          }, 0);
        const groupHeight = groupHeights[group.businessKey] || this.nodeHeight;
        const sourceTopY = sourcePosition
          ? sourcePosition.y - this.nodeHeight / 2
          : topPadding + groupHeight + 45;
        const groupTopY = sourceTopY - 45 - groupsBeforeHeight - groupHeight;
        const groupPositions = this.buildSubProcessPositions(
          group,
          sourcePosition,
          groupTopY,
          columnWidth,
          rowHeight
        );

        Object.keys(groupPositions).forEach((nodeId) => {
          const position = groupPositions[nodeId];
          nodePositions[nodeId] = {
            x: position.x,
            y: position.y,
          };
          nodeDepths[nodeId] = sourceDepth + position.depthOffset;
        });
      });

      const maxX = Math.max(
        leftPadding + this.nodeWidth,
        ...Object.values(nodePositions).map((pos) => pos.x + this.nodeWidth / 2)
      );
      const maxY = Math.max(
        topPadding + mainLayoutHeight,
        ...Object.values(nodePositions).map(
          (pos) => pos.y + this.nodeHeight / 2
        )
      );
      // 检测同行非相邻前向边（需要走顶部弧线路由，避免穿越中间节点）
      const nonAdjacentSameLevelEdges = this.validEdges.filter((edge) => {
        if (edge.edgeType !== "forward") return false;
        const fPos = nodePositions[edge.from];
        const tPos = nodePositions[edge.to];
        if (!fPos || !tPos) return false;
        const sameLevelY = Math.abs(fPos.y - tPos.y) < 5;
        const fDepth = nodeDepths[edge.from] || 0;
        const tDepth = nodeDepths[edge.to] || 0;
        return sameLevelY && Math.abs(tDepth - fDepth) > 1;
      });

      const allTopEdges = [...this.topLoopEdges, ...nonAdjacentSameLevelEdges];
      const topLoopMargin = this.calculateLoopMargin(allTopEdges, nodePositions);
      const bottomLoopMargin = 0;
      const totalWidth = Math.max(300, maxX + leftPadding);
      const totalHeight = Math.max(
        150,
        maxY + topPadding + topLoopMargin + bottomLoopMargin
      );

      return {
        nodePositions,
        nodeDepths,
        columns: orderedColumns,
        width: totalWidth,
        height: totalHeight,
        topLoopEdges: this.topLoopEdges,
        nonAdjacentSameLevelEdges,
        allTopEdges,
        bottomLoopEdges: this.bottomLoopEdges,
        topLoopMargin,
        bottomLoopMargin,
      };
    },

    /**
     * 带位置信息的节点列表
     * 位置会根据环线顶部边距进行偏移
     */
    positionedNodes() {
      const { nodePositions } = this.treeLayout;
      const topOffset = this.treeLayout.topLoopMargin;
      return this.flowNodes.map((node) => {
        const originalPos = nodePositions[node.nodeId] || { x: 0, y: 0 };
        return {
          ...node,
          position: {
            x: originalPos.x,
            y: originalPos.y + topOffset,
          },
        };
      });
    },

    /**
     * SVG 宽度
     */
    svgWidth() {
      return Math.max(this.treeLayout.width, 300);
    },

    /**
     * SVG 高度
     * 增加顶部空间以容纳环线
     */
    svgHeight() {
      return Math.max(this.treeLayout.height, 150);
    },

    /**
     * 树状布局包装器样式
     */
    wrapperStyle() {
      return {
        width: `${this.svgWidth}px`,
        height: `${this.svgHeight}px`,
        position: "relative",
      };
    },

    viewportStyle() {
      return {
        width: `${this.svgWidth}px`,
        height: `${this.svgHeight}px`,
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`,
        transformOrigin: "0 0",
      };
    },

    zoomPercent() {
      return `${Math.round(this.scale * 100)}%`;
    },

    /**
     * 计算所有连线（水平方向，支持分支和合并）
     * 环线（回边）从节点上方中心连接
     */
    connections() {
      const {
        nodePositions,
        nodeDepths,
        topLoopEdges,
        nonAdjacentSameLevelEdges,
        allTopEdges,
        bottomLoopEdges,
        topLoopMargin,
      } = this.treeLayout;
      const connections = [];
      const nodeMap = this.nodeMap;

      if (!nodePositions || Object.keys(nodePositions).length === 0) {
        return connections;
      }

      this.validEdges.forEach((edge) => {
        const fromId = edge.from;
        const toId = edge.to;
        const fromNode = nodeMap[fromId];
        const toNode = nodeMap[toId];
        const originalFromPos = nodePositions[fromId];
        const originalToPos = nodePositions[toId];

        if (!originalFromPos || !originalToPos || !fromNode || !toNode) return;

        const fromPos = {
          x: originalFromPos.x,
          y: originalFromPos.y + topLoopMargin,
        };
        const toPos = {
          x: originalToPos.x,
          y: originalToPos.y + topLoopMargin,
        };

        const isCompleted = this.getDisplayStatus(fromNode) === "completed";
        const color = this.isRejectStyledEdge(edge)
          ? "#ff4d4f"
          : isCompleted
          ? "#1890ff"
          : "#d9d9d9";
        const marker = this.isRejectStyledEdge(edge)
          ? "url(#arrowhead-reject-h)"
          : isCompleted
          ? "url(#arrowhead-completed-h)"
          : "url(#arrowhead-pending-h)";
        const fromDepth = nodeDepths[fromId] || 0;
        const toDepth = nodeDepths[toId] || 0;
        const isTopLoopEdge = topLoopEdges.some(
          (loopEdge) => loopEdge.from === fromId && loopEdge.to === toId
        );
        const isBottomLoopEdge = bottomLoopEdges.some(
          (loopEdge) => loopEdge.from === fromId && loopEdge.to === toId
        );
        const isNonAdjacentSameLevelEdge = nonAdjacentSameLevelEdges.some(
          (e) => e.from === fromId && e.to === toId
        );
        const pairRole = this.getPairRole(edge);
        const subSourceRole = this.getSubSourceEdgeRole(edge);
        const crossPairGroup = this.getCrossPairGroup(fromPos, toPos);

        let path;
        let labelX;
        let labelY;
        let labelClass = "";

        if (subSourceRole === "launch") {
          const routePoints = this.getSubProcessLaunchPoints(
            fromNode,
            toNode,
            fromPos,
            toPos,
            "oneFifth"
          );
          path = `M ${routePoints.start.x} ${routePoints.start.y}
                  L ${routePoints.corner.x} ${routePoints.corner.y}
                  L ${routePoints.end.x} ${routePoints.end.y}`;
          labelX = (routePoints.corner.x + routePoints.end.x) / 2 - 18;
          labelY = routePoints.corner.y + 14;
        } else if (subSourceRole === "launch-reject") {
          const routePoints = this.getSubProcessLaunchRejectPoints(
            fromNode,
            toNode,
            fromPos,
            toPos
          );
          path = `M ${routePoints.start.x} ${routePoints.start.y}
                  L ${routePoints.corner.x} ${routePoints.corner.y}
                  L ${routePoints.end.x} ${routePoints.end.y}`;
          labelX = (routePoints.start.x + routePoints.corner.x) / 2 + 18;
          labelY = routePoints.start.y - 8;
          labelClass = "edge-label-reject";
        } else if (subSourceRole === "return") {
          const routePoints = this.getSubProcessReturnPoints(
            fromNode,
            toNode,
            fromPos,
            toPos,
            "threeFifths"
          );
          path = `M ${routePoints.start.x} ${routePoints.start.y}
                  L ${routePoints.corner.x} ${routePoints.corner.y}
                  L ${routePoints.end.x} ${routePoints.end.y}`;
          labelX = routePoints.end.x + 28;
          labelY = (routePoints.start.y + routePoints.end.y) / 2 + 8;
        } else if (subSourceRole === "return-reject") {
          const routePoints = this.getSubProcessReturnRejectPoints(
            fromNode,
            toNode,
            fromPos,
            toPos
          );
          path = `M ${routePoints.start.x} ${routePoints.start.y}
                  L ${routePoints.corner.x} ${routePoints.corner.y}
                  L ${routePoints.end.x} ${routePoints.end.y}`;
          labelX = (routePoints.start.x + routePoints.corner.x) / 2;
          labelY = routePoints.start.y - 8;
          labelClass = "edge-label-reject";
        } else if (isTopLoopEdge) {
          const startPoint = this.getNodeAnchor(
            fromNode,
            fromPos,
            "top",
            "center"
          );
          const endPoint = this.getNodeAnchor(toNode, toPos, "top", "center");
          const horizontalDistance = Math.abs(startPoint.x - endPoint.x);
          const loopIndex = this.getLoopEdgeIndex(topLoopEdges, edge);
          const turnY =
            Math.min(startPoint.y, endPoint.y) -
            Math.max(30, horizontalDistance * 0.12) -
            loopIndex * 20;

          path = `M ${startPoint.x} ${startPoint.y}
                  L ${startPoint.x} ${turnY}
                  L ${endPoint.x} ${turnY}
                  L ${endPoint.x} ${endPoint.y}`;
          labelX =
            (startPoint.x + endPoint.x) / 2 +
            (pairRole === "paired-reject" ? 18 : -18);
          labelY = pairRole === "paired-reject" ? turnY + 14 : turnY - 8;
          labelClass = pairRole === "paired-reject" ? "edge-label-reject" : "";
        } else if (isBottomLoopEdge) {
          const routePoints = this.getSubProcessReturnPoints(
            fromNode,
            toNode,
            fromPos,
            toPos,
            "threeFifths"
          );
          path = `M ${routePoints.start.x} ${routePoints.start.y}
                  L ${routePoints.corner.x} ${routePoints.corner.y}
                  L ${routePoints.end.x} ${routePoints.end.y}`;
          labelX = routePoints.end.x + 28;
          labelY = (routePoints.start.y + routePoints.end.y) / 2 + 8;
        } else if (isNonAdjacentSameLevelEdge) {
          // 同行非相邻节点（如 a1->c1，中间有 b1）：从起点顶边缘绕到终点顶边缘，避免穿越中间节点
          const startPoint = this.getNodeAnchor(fromNode, fromPos, "top", "center");
          const endPoint = this.getNodeAnchor(toNode, toPos, "top", "center");
          const horizontalDistance = Math.abs(startPoint.x - endPoint.x);
          const loopIndex = this.getLoopEdgeIndex(allTopEdges, edge);
          /**
           * 非相邻同行前向边的上拐高度限制为“节点间隙的一半”，
           * 避免弧线抬升过高导致视觉上不贴近该行节点。
           */
          const riseByDistance =
            Math.max(30, horizontalDistance * 0.12) + loopIndex * 20;
          const maxRise = Math.max(4, this.nodeGap / 2);
          const limitedRise = Math.min(riseByDistance, maxRise);
          const turnY = Math.min(startPoint.y, endPoint.y) - limitedRise;
          path = `M ${startPoint.x} ${startPoint.y}
                  L ${startPoint.x} ${turnY}
                  L ${endPoint.x} ${turnY}
                  L ${endPoint.x} ${endPoint.y}`;
          labelX =
            (startPoint.x + endPoint.x) / 2 +
            (pairRole === "paired-reject" ? 18 : -18);
          labelY = pairRole === "paired-reject" ? turnY + 14 : turnY - 8;
          labelClass = pairRole === "paired-reject" ? "edge-label-reject" : "";
        } else {
          const mainEdgePoints = this.getMainFlowEdgePoints(
            fromNode,
            toNode,
            fromPos,
            toPos
          );
          const mainStartPoint = mainEdgePoints.start;
          const mainEndPoint = mainEdgePoints.end;
          const startX = mainStartPoint.x;
          const startY = mainStartPoint.y;
          const endX = mainEndPoint.x;
          const endY = mainEndPoint.y;

          if (this.isEnterBridgeEdge(edge)) {
            const routePoints = this.getSubProcessLaunchPoints(
              fromNode,
              toNode,
              fromPos,
              toPos,
              "oneFifth"
            );
            path = `M ${routePoints.start.x} ${routePoints.start.y}
                    L ${routePoints.corner.x} ${routePoints.corner.y}
                    L ${routePoints.end.x} ${routePoints.end.y}`;
            labelX =
              (routePoints.corner.x + routePoints.end.x) / 2 +
              (pairRole === "paired-reject" ? 18 : -18);
            labelY =
              routePoints.corner.y + (pairRole === "paired-reject" ? 14 : -8);
            labelClass = pairRole === "paired-reject" ? "edge-label-reject" : "";
          } else if (
            fromNode.processType === "sub" ||
            toNode.processType === "sub"
          ) {
            const edgePoints = this.getSubProcessEdgePoints(
              fromNode,
              toNode,
              fromPos,
              toPos
            );
            if (edgePoints.routeType === "vertical") {
              const verticalStart =
                pairRole === "paired-submit"
                  ? this.getNodeAnchor(
                      fromNode,
                      fromPos,
                      edgePoints.start.y > edgePoints.end.y ? "top" : "bottom",
                      "oneThird"
                    )
                  : pairRole === "paired-reject"
                  ? this.getNodeAnchor(
                      fromNode,
                      fromPos,
                      edgePoints.start.y > edgePoints.end.y ? "top" : "bottom",
                      "twoThirds"
                    )
                  : edgePoints.start;
              const verticalEnd =
                pairRole === "paired-submit"
                  ? this.getNodeAnchor(
                      toNode,
                      toPos,
                      edgePoints.start.y > edgePoints.end.y ? "bottom" : "top",
                      "oneThird"
                    )
                  : pairRole === "paired-reject"
                  ? this.getNodeAnchor(
                      toNode,
                      toPos,
                      edgePoints.start.y > edgePoints.end.y ? "bottom" : "top",
                      "twoThirds"
                    )
                  : edgePoints.end;
              path = `M ${verticalStart.x} ${verticalStart.y}
                      L ${verticalEnd.x} ${verticalEnd.y}`;
              labelX =
                verticalStart.x + (pairRole === "paired-reject" ? 24 : -24);
              labelY =
                verticalStart.y * 0.65 +
                verticalEnd.y * 0.35 +
                (pairRole === "paired-reject" ? 12 : -4);
              labelClass = pairRole === "paired-reject" ? "edge-label-reject" : "";
            } else {
              const horizontalStart =
                pairRole === "paired-submit"
                  ? this.getNodeAnchor(
                      fromNode,
                      fromPos,
                      fromPos.x <= toPos.x ? "right" : "left",
                      "oneThird"
                    )
                  : pairRole === "paired-reject"
                  ? this.getNodeAnchor(
                      fromNode,
                      fromPos,
                      fromPos.x <= toPos.x ? "right" : "left",
                      "twoThirds"
                    )
                  : edgePoints.start;
              const horizontalEnd =
                pairRole === "paired-submit"
                  ? this.getNodeAnchor(
                      toNode,
                      toPos,
                      fromPos.x <= toPos.x ? "left" : "right",
                      "oneThird"
                    )
                  : pairRole === "paired-reject"
                  ? this.getNodeAnchor(
                      toNode,
                      toPos,
                      fromPos.x <= toPos.x ? "left" : "right",
                      "twoThirds"
                    )
                  : edgePoints.end;
              path = `M ${horizontalStart.x} ${horizontalStart.y}
                      L ${horizontalEnd.x} ${horizontalEnd.y}`;
              labelX =
                (horizontalStart.x + horizontalEnd.x) / 2 +
                (pairRole === "paired-reject" ? 18 : -18);
              labelY =
                horizontalStart.y +
                (pairRole === "paired-reject" ? 14 : -8);
              labelClass = pairRole === "paired-reject" ? "edge-label-reject" : "";
            }
          } else if (mainEdgePoints.routeType === "straight") {
            const horizontalStart =
              pairRole === "paired-submit"
                ? this.getNodeAnchor(
                    fromNode,
                    fromPos,
                    fromPos.x <= toPos.x ? "right" : "left",
                    "oneThird"
                  )
                : pairRole === "paired-reject"
                ? this.getNodeAnchor(
                    fromNode,
                    fromPos,
                    fromPos.x <= toPos.x ? "right" : "left",
                    "twoThirds"
                  )
                : mainStartPoint;
            const horizontalEnd =
              pairRole === "paired-submit"
                ? this.getNodeAnchor(
                    toNode,
                    toPos,
                    fromPos.x <= toPos.x ? "left" : "right",
                    "oneThird"
                  )
                : pairRole === "paired-reject"
                ? this.getNodeAnchor(
                    toNode,
                    toPos,
                    fromPos.x <= toPos.x ? "left" : "right",
                    "twoThirds"
                  )
                : mainEndPoint;
            path = `M ${horizontalStart.x} ${horizontalStart.y} L ${horizontalEnd.x} ${horizontalEnd.y}`;
            labelX =
              (horizontalStart.x + horizontalEnd.x) / 2 +
              (pairRole === "paired-reject" ? 18 : -18);
            labelY =
              horizontalStart.y +
              (pairRole === "paired-reject" ? 14 : -8);
            labelClass = pairRole === "paired-reject" ? "edge-label-reject" : "";
          } else if (mainEdgePoints.routeType === "downCross") {
            const pairedDownStart =
              crossPairGroup === "lu-rd" && pairRole === "paired-submit"
                ? this.getNodeAnchor(fromNode, fromPos, "bottom", "oneThird")
                : crossPairGroup === "ld-ru" && pairRole === "paired-reject"
                ? this.getNodeAnchor(fromNode, fromPos, "bottom", "oneThird")
                : crossPairGroup === "lu-rd" && pairRole === "paired-reject"
                ? this.getNodeAnchor(fromNode, fromPos, "left", "oneThird")
                : null;
            const pairedDownEnd =
              crossPairGroup === "lu-rd" && pairRole === "paired-submit"
                ? this.getNodeAnchor(toNode, toPos, "left", "twoThirds")
                : crossPairGroup === "ld-ru" && pairRole === "paired-reject"
                ? this.getNodeAnchor(toNode, toPos, "right", "oneThird")
                : crossPairGroup === "lu-rd" && pairRole === "paired-reject"
                ? this.getNodeAnchor(toNode, toPos, "bottom", "twoThirds")
                : null;
            const lineOffset =
              pairRole === "paired-submit"
                ? -8
                : pairRole === "paired-reject"
                ? 8
                : 0;
            if (pairedDownStart && pairedDownEnd) {
              if (pairRole === "paired-submit") {
                path = `M ${pairedDownStart.x} ${pairedDownStart.y}
                        L ${pairedDownStart.x} ${pairedDownEnd.y}
                        L ${pairedDownEnd.x} ${pairedDownEnd.y}`;
                labelX =
                  (pairedDownStart.x + pairedDownEnd.x) / 2 - 18;
                labelY = pairedDownEnd.y + 14;
              } else {
                if (crossPairGroup === "ld-ru") {
                  path = `M ${pairedDownStart.x} ${pairedDownStart.y}
                          L ${pairedDownStart.x} ${pairedDownEnd.y}
                          L ${pairedDownEnd.x} ${pairedDownEnd.y}`;
                  labelX =
                    (pairedDownStart.x + pairedDownEnd.x) / 2 + 18;
                  labelY = pairedDownEnd.y - 8;
                } else {
                  path = `M ${pairedDownStart.x} ${pairedDownStart.y}
                          L ${pairedDownEnd.x} ${pairedDownStart.y}
                          L ${pairedDownEnd.x} ${pairedDownEnd.y}`;
                  labelX =
                    (pairedDownStart.x + pairedDownEnd.x) / 2 + 18;
                  labelY = pairedDownStart.y - 8;
                }
              }
            } else {
              const turnY = endY + lineOffset;
              path = `M ${startX} ${startY}
                      L ${startX} ${turnY}
                      L ${endX} ${turnY}
                      L ${endX} ${endY}`;
              labelX =
                (startX + endX) / 2 + (pairRole === "paired-reject" ? 18 : -18);
              labelY = turnY + (pairRole === "paired-reject" ? 14 : -8);
            }
            labelClass = pairRole === "paired-reject" ? "edge-label-reject" : "";
          } else {
            const pairedUpStart =
              crossPairGroup === "ld-ru" && pairRole === "paired-submit"
                ? this.getNodeAnchor(fromNode, fromPos, "right", "twoThirds")
                : crossPairGroup === "lu-rd" && pairRole === "paired-reject"
                ? this.getNodeAnchor(fromNode, fromPos, "left", "oneThird")
                : crossPairGroup === "ld-ru" && pairRole === "paired-reject"
                ? this.getNodeAnchor(fromNode, fromPos, "bottom", "oneThird")
                : null;
            const pairedUpEnd =
              crossPairGroup === "ld-ru" && pairRole === "paired-submit"
                ? this.getNodeAnchor(toNode, toPos, "bottom", "twoThirds")
                : crossPairGroup === "lu-rd" && pairRole === "paired-reject"
                ? this.getNodeAnchor(toNode, toPos, "bottom", "twoThirds")
                : crossPairGroup === "ld-ru" && pairRole === "paired-reject"
                ? this.getNodeAnchor(toNode, toPos, "right", "oneThird")
                : null;
            const lineOffset =
              pairRole === "paired-submit"
                ? -8
                : pairRole === "paired-reject"
                ? 8
                : 0;
            if (pairedUpStart && pairedUpEnd) {
              if (pairRole === "paired-submit") {
                path = `M ${pairedUpStart.x} ${pairedUpStart.y}
                        L ${pairedUpEnd.x} ${pairedUpStart.y}
                        L ${pairedUpEnd.x} ${pairedUpEnd.y}`;
                labelX =
                  (pairedUpStart.x + pairedUpEnd.x) / 2 - 18;
                labelY = pairedUpStart.y + 14;
              } else {
                path = `M ${pairedUpStart.x} ${pairedUpStart.y}
                        L ${pairedUpEnd.x} ${pairedUpStart.y}
                        L ${pairedUpEnd.x} ${pairedUpEnd.y}`;
                labelX =
                  (pairedUpStart.x + pairedUpEnd.x) / 2 + 18;
                labelY = pairedUpStart.y - 8;
              }
            } else {
              path =
                lineOffset === 0
                  ? `M ${startX} ${startY}
                     L ${endX} ${startY}
                     L ${endX} ${endY}`
                  : `M ${startX} ${startY}
                     L ${startX} ${startY + lineOffset}
                     L ${endX} ${startY + lineOffset}
                     L ${endX} ${endY}`;
              labelX =
                (startX + endX) / 2 + (pairRole === "paired-reject" ? 18 : -18);
              labelY =
                startY + lineOffset + (pairRole === "paired-reject" ? 14 : -8);
            }
            labelClass = pairRole === "paired-reject" ? "edge-label-reject" : "";
          }
        }

        connections.push({
          path,
          color,
          markerEnd: marker,
          fromId,
          toId,
          isBackEdge: isTopLoopEdge || isBottomLoopEdge,
          labelText: this.formatEdgeLabel(edge),
          labelX,
          labelY,
          labelClass,
          edgeType: edge.edgeType,
          startCircle: this.buildStartCircle(path, color),
        });
      });

      return connections;
    },
  },
  watch: {
    questionId: {
      handler(newVal) {
        if (newVal) {
          this.loadFlowDiagram();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (!this.questionId) {
      const storedId = localStorage.getItem("problemId");
      if (storedId) {
        this.loadFlowDiagramById(storedId);
      }
    }
    this.activeNames = []
    document.addEventListener("mousemove", this.handlePanMove);
    document.addEventListener("mouseup", this.handlePanEnd);
  },
  beforeDestroy() {
    document.removeEventListener("mousemove", this.handlePanMove);
    document.removeEventListener("mouseup", this.handlePanEnd);
  },
  methods: {
    applyFlowDiagramData(data) {
      this.flowNodes = this.normalizeNodes(data.nodes || []).sort(
        (a, b) => a.orderNum - b.orderNum
      );
      this.flowEdges = this.normalizeEdges(data.edges || []);
      this.$nextTick(() => {
        this.fitViewport();
      });
    },

    resetFlowDiagramData() {
      this.flowNodes = [];
      this.flowEdges = [];
    },

    async loadFlowDiagram() {
      if (!this.questionId) return;
      try {
        const res = await getFlowDiagram(this.questionId);
        if (res.status === "0" && res.data) {
          this.applyFlowDiagramData(res.data);
        } else {
          this.resetFlowDiagramData();
        }
      } catch (error) {
        console.error("获取流程图数据失败:", error);
        this.resetFlowDiagramData();
      }
    },

    async loadFlowDiagramById(questionId) {
      if (!questionId) return;
      try {
        const res = await getFlowDiagram(questionId);
        if (res.status === "0" && res.data) {
          this.applyFlowDiagramData(res.data);
        } else {
          this.resetFlowDiagramData();
        }
      } catch (error) {
        console.error("获取流程图数据失败:", error);
        this.resetFlowDiagramData();
      }
    },

    /**
     * 获取节点样式（绝对定位）
     */
    getNodeStyle(node) {
      const pos = node.position;
      return {
        left: `${pos.x - this.nodeWidth / 2}px`,
        top: `${pos.y - this.nodeHeight / 2}px`,
        width: `${this.nodeWidth}px`,
        height: `${this.nodeHeight}px`,
      };
    },

    getViewportCenterClientPoint() {
      const viewportShell = this.$refs.viewportShell;
      if (!viewportShell) {
        return { x: 0, y: 0 };
      }
      const rect = viewportShell.getBoundingClientRect();
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
    },

    clampScale(nextScale) {
      return Math.min(2.5, Math.max(0.4, nextScale));
    },

    zoomAtPoint(nextScale, clientX, clientY) {
      const viewportShell = this.$refs.viewportShell;
      if (!viewportShell) {
        return;
      }
      const rect = viewportShell.getBoundingClientRect();
      const clampedScale = this.clampScale(nextScale);
      const offsetX = clientX - rect.left;
      const offsetY = clientY - rect.top;
      const contentX = (offsetX - this.translateX) / this.scale;
      const contentY = (offsetY - this.translateY) / this.scale;
      this.scale = clampedScale;
      this.translateX = offsetX - contentX * clampedScale;
      this.translateY = offsetY - contentY * clampedScale;
    },

    zoomIn() {
      const centerPoint = this.getViewportCenterClientPoint();
      this.zoomAtPoint(this.scale + 0.1, centerPoint.x, centerPoint.y);
    },

    zoomOut() {
      const centerPoint = this.getViewportCenterClientPoint();
      this.zoomAtPoint(this.scale - 0.1, centerPoint.x, centerPoint.y);
    },

    resetViewport() {
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.$nextTick(() => {
        this.fitViewport();
      });
    },

    fitViewport() {
      const viewportShell = this.$refs.viewportShell;
      if (!viewportShell || !this.svgWidth || !this.svgHeight) {
        return;
      }
      const padding = 24;
      const availableWidth = Math.max(viewportShell.clientWidth - padding * 2, 1);
      const availableHeight = Math.max(viewportShell.clientHeight - padding * 2, 1);
      const widthScale = availableWidth / this.svgWidth;
      const heightScale = availableHeight / this.svgHeight;
      const fitScale = this.clampScale(Math.min(widthScale, heightScale, 1));
      this.scale = fitScale;
      this.translateX = (viewportShell.clientWidth - this.svgWidth * fitScale) / 2;
      this.translateY = (viewportShell.clientHeight - this.svgHeight * fitScale) / 2;
    },

    handleWheel(event) {
      if (!event.ctrlKey && !event.metaKey) {
        return;
      }
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.1 : 0.1;
      this.zoomAtPoint(this.scale + delta, event.clientX, event.clientY);
    },

    handlePanStart(event) {
      if (event.button !== 0) {
        return;
      }
      if (
        event.target.closest(".tree-node") ||
        event.target.closest(".flow-toolbar")
      ) {
        return;
      }
      this.isPanning = true;
      this.panStartX = event.clientX;
      this.panStartY = event.clientY;
      this.panOriginX = this.translateX;
      this.panOriginY = this.translateY;
    },

    handlePanMove(event) {
      if (!this.isPanning) {
        return;
      }
      const deltaX = event.clientX - this.panStartX;
      const deltaY = event.clientY - this.panStartY;
      this.translateX = this.panOriginX + deltaX;
      this.translateY = this.panOriginY + deltaY;
    },

    handlePanEnd() {
      this.isPanning = false;
    },

    formatNodeName(node) {
      const roleName = node.roleName || "";
      const personName = node.assigneeName || "";
      if (personName) {
        return `${roleName}(${personName})`;
      }
      return roleName || "待分配";
    },

    getNodeAction(nodeName) {
      return nodeName || "";
    },

    getDisplayStatus(node) {
      return node.displayStatus || node.status || "pending";
    },

    getNodeClass(node) {
      return {
        "node-completed": this.getDisplayStatus(node) === "completed",
        "node-pending": this.getDisplayStatus(node) === "pending",
      };
    },

    formatEdgeLabel(edge) {
      const edgeName =
        edge.bridgeType === "return-reject"
          ? "驳回到主流程"
          : edge.edgeName || "";
      const historyCount = Number(edge.historyCount || 1);
      if (edgeName && historyCount > 1) {
        return `${edgeName} x${historyCount}`;
      }
      if (edgeName) {
        return edgeName;
      }
      if (historyCount > 1) {
        return `x${historyCount}`;
      }
      return "";
    },

    buildStartCircle(path, color) {
      const startPoint = this.getPathStartPoint(path);
      if (!startPoint) {
        return null;
      }
      return {
        x: startPoint.x,
        y: startPoint.y,
        radius: 2.5,
        fill: "#ffffff",
        stroke: color,
        strokeWidth: 1.25,
      };
    },

    getPathStartPoint(path) {
      if (!path) {
        return null;
      }
      const matched = path.match(/M\s*([-\d.]+)\s+([-\d.]+)/);
      if (!matched) {
        return null;
      }
      return {
        x: Number(matched[1]),
        y: Number(matched[2]),
      };
    },

    getPairRole(edge) {
      const counterpart = this.getCounterpartEdge(edge);
      if (!counterpart) {
        return "normal";
      }
      if (edge.edgeType === "reject" && counterpart.edgeType !== "reject") {
        return "paired-reject";
      }
      if (edge.edgeType !== "reject" && counterpart.edgeType === "reject") {
        return "paired-submit";
      }
      return "normal";
    },

    getCounterpartEdge(edge) {
      return this.validEdges.find((item) => {
        return (
          item.from === edge.to &&
          item.to === edge.from &&
          item.edgeType !== edge.edgeType
        );
      });
    },

    getCrossPairGroup(fromPos, toPos) {
      if (!fromPos || !toPos) {
        return "";
      }
      const sameX = Math.abs(fromPos.x - toPos.x) < 5;
      const sameY = Math.abs(fromPos.y - toPos.y) < 5;
      if (sameX || sameY) {
        return "";
      }
      const xAscending = fromPos.x < toPos.x;
      const yAscending = fromPos.y < toPos.y;
      return xAscending === yAscending ? "lu-rd" : "ld-ru";
    },

    normalizeEdges(edges) {
      return normalizeFlowEdges(edges);
    },

    normalizeNodes(nodes) {
      return normalizeFlowNodes(nodes);
    },

    isEnterBridgeEdge(edge) {
      if (!edge || edge.edgeType !== "bridge") {
        return false;
      }
      if (edge.bridgeType) {
        return edge.bridgeType === "launch";
      }
      const fromNode = this.nodeMap[edge.from];
      const toNode = this.nodeMap[edge.to];
      return (
        fromNode &&
        toNode &&
        fromNode.processType === "main" &&
        toNode.processType === "sub"
      );
    },

    isReturnBridgeEdge(edge) {
      if (!edge || edge.edgeType !== "bridge") {
        return false;
      }
      if (edge.bridgeType) {
        return edge.bridgeType === "return" || edge.bridgeType === "return-reject";
      }
      const fromNode = this.nodeMap[edge.from];
      const toNode = this.nodeMap[edge.to];
      return (
        fromNode &&
        toNode &&
        fromNode.processType === "sub" &&
        toNode.processType === "main"
      );
    },

    getSubSourceEdgeRole(edge) {
      if (!edge) {
        return "";
      }
      if (edge.edgeType === "bridge" && edge.bridgeType) {
        return edge.bridgeType;
      }
      if (this.isEnterBridgeEdge(edge)) {
        return "launch";
      }
      if (this.isReturnBridgeEdge(edge)) {
        return "return";
      }
      if (edge.edgeType !== "reject") {
        return "";
      }
      const counterpart = this.getCounterpartEdge(edge);
      if (!counterpart) {
        return "";
      }
      if (this.isEnterBridgeEdge(counterpart)) {
        return "launch-reject";
      }
      if (this.isReturnBridgeEdge(counterpart)) {
        return "return-reject";
      }
      return "";
    },

    isRejectStyledEdge(edge) {
      return (
        !!edge &&
        (edge.edgeType === "reject" || edge.bridgeType === "return-reject")
      );
    },

    compareNodes(leftNode, rightNode) {
      return compareFlowNodes(leftNode, rightNode);
    },

    calculateLoopMargin(loopEdges, nodePositions) {
      if (!loopEdges || loopEdges.length === 0) {
        return 0;
      }
      let maxMargin = 0;
      loopEdges.forEach((edge, index) => {
        const fromPos = nodePositions[edge.from];
        const toPos = nodePositions[edge.to];
        if (!fromPos || !toPos) {
          return;
        }
        const horizontalDistance = Math.abs(fromPos.x - toPos.x);
        const loopHeight =
          Math.max(40, horizontalDistance * 0.12) + index * 20 + 30;
        maxMargin = Math.max(maxMargin, loopHeight);
      });
      return maxMargin;
    },

    getLoopEdgeIndex(loopEdges, currentEdge) {
      const sameDirectionEdges = loopEdges.filter((edge) => {
        return edge.from === currentEdge.from && edge.to === currentEdge.to;
      });
      const currentIndex = sameDirectionEdges.findIndex((edge) => {
        return (
          edge.from === currentEdge.from &&
          edge.to === currentEdge.to &&
          edge.businessKey === currentEdge.businessKey
        );
      });
      return currentIndex < 0 ? 0 : currentIndex;
    },

    buildDepthInfo(nodes, edges) {
      return buildFlowDepthInfo(nodes, edges);
    },

    getSubProcessGroups() {
      return buildSubProcessGroups({
        flowNodes: this.flowNodes,
        enterBridgeEdges: this.enterBridgeEdges,
        validEdges: this.validEdges,
        nodeMap: this.nodeMap,
      });
    },

    buildSourceGroupMap(groups) {
      return buildSubProcessSourceGroupMap(groups);
    },

    calculateSubGroupHeight(group) {
      if (!group || !group.nodes || group.nodes.length === 0) {
        return this.nodeHeight;
      }
      const sortedNodes = this.sortSubProcessNodes(group);
      const rowHeight = this.nodeHeight + this.nodeGap;
      if (sortedNodes.length <= 1) {
        return this.nodeHeight;
      }
      if (sortedNodes.length === 2) {
        return rowHeight * 2 - this.nodeGap;
      }
      return rowHeight * 2 - this.nodeGap;
    },

    getNodeGroupKey(node) {
      return getNodeGroupKey(node);
    },

    getNodeAnchor(node, position, side, slot) {
      void node;
      return getFlowNodeAnchor(
        position,
        this.nodeWidth,
        this.nodeHeight,
        side,
        slot
      );
    },

    getSubReturnSourceAnchor(node, position) {
      void node;
      return getSubProcessReturnSourceAnchor(
        position,
        this.nodeWidth,
        this.nodeHeight
      );
    },

    getSubProcessLaunchPoints(fromNode, toNode, fromPos, toPos, slot = "oneFifth") {
      void fromNode;
      void toNode;
      return getSubProcessLaunchRoute(
        fromPos,
        toPos,
        this.nodeWidth,
        this.nodeHeight,
        slot
      );
    },

    getSubProcessLaunchRejectPoints(fromNode, toNode, fromPos, toPos) {
      void fromNode;
      void toNode;
      return getSubProcessLaunchRejectRoute(
        fromPos,
        toPos,
        this.nodeWidth,
        this.nodeHeight
      );
    },

    getSubProcessReturnPoints(fromNode, toNode, fromPos, toPos, slot = "threeFifths") {
      void fromNode;
      void toNode;
      return getSubProcessReturnRoute(
        fromPos,
        toPos,
        this.nodeWidth,
        this.nodeHeight,
        slot
      );
    },

    getSubProcessReturnRejectPoints(fromNode, toNode, fromPos, toPos) {
      void fromNode;
      void toNode;
      return getSubProcessReturnRejectRoute(
        fromPos,
        toPos,
        this.nodeWidth,
        this.nodeHeight
      );
    },

    sortSubProcessNodes(group) {
      const depthInfo = this.buildDepthInfo(group.nodes, group.forwardEdges);
      return [...group.nodes].sort((left, right) => {
        const leftDepth = depthInfo.nodeDepths[left.nodeId] || 0;
        const rightDepth = depthInfo.nodeDepths[right.nodeId] || 0;
        if (leftDepth !== rightDepth) {
          return leftDepth - rightDepth;
        }
        return this.compareNodes(left, right);
      });
    },

    buildSubProcessPositions(
      group,
      sourcePosition,
      groupTopY,
      columnWidth,
      rowHeight
    ) {
      const sortedNodes = this.sortSubProcessNodes(group);
      const positions = {};
      const sourceX = sourcePosition ? sourcePosition.x : columnWidth;
      const topRowY = groupTopY + this.nodeHeight / 2;
      const bottomRowY = groupTopY + rowHeight + this.nodeHeight / 2;
      const leftColumnX = sourceX - columnWidth;
      const sourceColumnX = sourceX;

      /**
       * 单节点子流程通常表示刚发起、尚未继续流转。
       * 这类场景需要挂在发起节点左上方，避免落在水平左侧影响阅读。
       */
      if (sortedNodes.length === 1) {
        const onlyNode = sortedNodes[0];
        positions[onlyNode.nodeId] = {
          x: leftColumnX,
          y: topRowY,
          depthOffset: 1,
        };
        return positions;
      }

      sortedNodes.forEach((node, index) => {
        let x = leftColumnX;
        let y = bottomRowY;
        let depthOffset = 1;

        if (index === 0) {
          x = leftColumnX;
          y = bottomRowY;
          depthOffset = 1;
        } else if (index === 1) {
          x = leftColumnX;
          y = topRowY;
          depthOffset = 1;
        } else if (index === 2) {
          x = sourceColumnX;
          y = topRowY;
          depthOffset = 2;
        } else {
          x = sourceColumnX + (index - 2) * columnWidth;
          y = topRowY;
          depthOffset = index;
        }

        positions[node.nodeId] = {
          x,
          y,
          depthOffset,
        };
      });

      return positions;
    },

    getSubProcessEdgePoints(fromNode, toNode, fromPos, toPos) {
      void fromNode;
      void toNode;
      return getSubProcessEdgeRoute(
        fromPos,
        toPos,
        this.nodeWidth,
        this.nodeHeight
      );
    },

    getMainFlowEdgePoints(fromNode, toNode, fromPos, toPos) {
      void fromNode;
      void toNode;
      return getMainFlowEdgeRoute(
        fromPos,
        toPos,
        this.nodeWidth,
        this.nodeHeight
      );
    },

    optimizeColumns(columns, rowIndexMap) {
      const orderedColumns = {};
      Object.keys(columns).forEach((depthKey) => {
        const depth = Number(depthKey);
        orderedColumns[depth] = [...columns[depth]].sort((left, right) => {
          const leftRowIndex =
            rowIndexMap[left.nodeId] ?? Number.MAX_SAFE_INTEGER;
          const rightRowIndex =
            rowIndexMap[right.nodeId] ?? Number.MAX_SAFE_INTEGER;
          if (leftRowIndex !== rightRowIndex) {
            return leftRowIndex - rightRowIndex;
          }
          return this.compareNodes(left, right);
        });
      });

      return orderedColumns;
    },

    buildLongestRemainingLengthMap(nodes, edges) {
      const nodeMap = {};
      const outgoingMap = {};
      nodes.forEach((node) => {
        nodeMap[node.nodeId] = node;
        outgoingMap[node.nodeId] = [];
      });
      (edges || []).forEach((edge) => {
        if (outgoingMap[edge.from]) {
          outgoingMap[edge.from].push(edge.to);
        }
      });
      Object.keys(outgoingMap).forEach((nodeId) => {
        outgoingMap[nodeId].sort((leftNodeId, rightNodeId) => {
          return this.compareNodes(nodeMap[leftNodeId], nodeMap[rightNodeId]);
        });
      });

      const memo = {};
      const walk = (nodeId) => {
        if (memo[nodeId] !== undefined) {
          return memo[nodeId];
        }
        const nextNodeIds = outgoingMap[nodeId] || [];
        if (nextNodeIds.length === 0) {
          memo[nodeId] = 1;
          return memo[nodeId];
        }
        memo[nodeId] =
          1 +
          Math.max(
            ...nextNodeIds.map((nextNodeId) => {
              return walk(nextNodeId);
            })
          );
        return memo[nodeId];
      };

      nodes.forEach((node) => {
        walk(node.nodeId);
      });

      return memo;
    },

    buildMainRowIndexMap(nodes, edges, longestRemainingLengthMap) {
      const nodeMap = {};
      const outgoingMap = {};
      const incomingMap = {};
      nodes.forEach((node) => {
        nodeMap[node.nodeId] = node;
        outgoingMap[node.nodeId] = [];
        incomingMap[node.nodeId] = [];
      });
      (edges || []).forEach((edge) => {
        if (outgoingMap[edge.from]) {
          outgoingMap[edge.from].push(edge.to);
        }
        if (incomingMap[edge.to]) {
          incomingMap[edge.to].push(edge.from);
        }
      });
      const rowIndexMap = {};
      const compareNodeIds = (leftNodeId, rightNodeId) => {
        const leftLength = longestRemainingLengthMap[leftNodeId] || 0;
        const rightLength = longestRemainingLengthMap[rightNodeId] || 0;
        if (leftLength !== rightLength) {
          return rightLength - leftLength;
        }
        return this.compareNodes(nodeMap[leftNodeId], nodeMap[rightNodeId]);
      };
      const sortNodeIdsByPriority = (nodeIds) => {
        return [...nodeIds].sort(compareNodeIds);
      };
      const primarySuccessorMap = {};
      Object.keys(outgoingMap).forEach((nodeId) => {
        outgoingMap[nodeId] = sortNodeIdsByPriority(outgoingMap[nodeId]);
        primarySuccessorMap[nodeId] = outgoingMap[nodeId][0] || null;
      });

      const layoutBranch = (startNodeId, baseRow) => {
        let currentNodeId = startNodeId;
        const walkedNodeIds = new Set();
        const sideBranchGroups = [];

        while (currentNodeId && !walkedNodeIds.has(currentNodeId)) {
          walkedNodeIds.add(currentNodeId);
          if (rowIndexMap[currentNodeId] === undefined) {
            rowIndexMap[currentNodeId] = baseRow;
          }

          const primarySuccessorId = primarySuccessorMap[currentNodeId];
          const sideBranchNodeIds = (outgoingMap[currentNodeId] || []).filter(
            (nextNodeId) => {
              return nextNodeId !== primarySuccessorId;
            }
          );
          if (sideBranchNodeIds.length > 0) {
            sideBranchGroups.push({
              branchNodeIds: sortNodeIdsByPriority(sideBranchNodeIds),
            });
          }

          if (
            !primarySuccessorId ||
            rowIndexMap[primarySuccessorId] !== undefined
          ) {
            break;
          }
          currentNodeId = primarySuccessorId;
        }

        let nextAvailableRow = baseRow + 1;
        [...sideBranchGroups].reverse().forEach((group) => {
          group.branchNodeIds.forEach((branchNodeId) => {
            if (rowIndexMap[branchNodeId] !== undefined) {
              return;
            }
            const branchLastRow = layoutBranch(branchNodeId, nextAvailableRow);
            nextAvailableRow = Math.max(nextAvailableRow, branchLastRow + 1);
          });
        });

        return Math.max(baseRow, nextAvailableRow - 1);
      };

      const rootNodeIds = sortNodeIdsByPriority(
        nodes
          .map((node) => node.nodeId)
          .filter((nodeId) => (incomingMap[nodeId] || []).length === 0)
      );
      let nextBaseRow = 0;
      rootNodeIds.forEach((rootNodeId) => {
        if (rowIndexMap[rootNodeId] !== undefined) {
          return;
        }
        const branchLastRow = layoutBranch(rootNodeId, nextBaseRow);
        nextBaseRow = branchLastRow + 1;
      });

      sortNodeIdsByPriority(
        nodes.map((node) => node.nodeId).filter((nodeId) => {
          return rowIndexMap[nodeId] === undefined;
        })
      ).forEach((nodeId) => {
        if (rowIndexMap[nodeId] !== undefined) {
          return;
        }
        const branchLastRow = layoutBranch(nodeId, nextBaseRow);
        nextBaseRow = branchLastRow + 1;
      });

      return rowIndexMap;
    },

    /**
     * 仅压缩四节点桥接结构（a1->b1->c1 与 a1->b13->c1）产生的空白行。
     * 只在中间行之间没有其他节点占位时生效，避免影响其他布局。
     *
     * @param {Array} edges 主流程前向边
     * @param {Object} rowIndexMap 节点行索引映射
     */
    compressFourNodeBridgeGapRow(edges, rowIndexMap, nodeDepths) {
      if (!edges || !rowIndexMap) {
        return;
      }
      const outgoingMap = {};
      (edges || []).forEach((edge) => {
        if (!outgoingMap[edge.from]) {
          outgoingMap[edge.from] = new Set();
        }
        outgoingMap[edge.from].add(edge.to);
      });

      const allNodeIds = Object.keys(rowIndexMap);
      allNodeIds.forEach((sourceNodeId) => {
        const sourceTargets = Array.from(outgoingMap[sourceNodeId] || []);
        if (sourceTargets.length < 2) {
          return;
        }
        const middleToTargets = {};
        sourceTargets.forEach((middleNodeId) => {
          const nextTargets = Array.from(outgoingMap[middleNodeId] || []);
          nextTargets.forEach((targetNodeId) => {
            if (targetNodeId === sourceNodeId || targetNodeId === middleNodeId) {
              return;
            }
            if (!middleToTargets[targetNodeId]) {
              middleToTargets[targetNodeId] = [];
            }
            middleToTargets[targetNodeId].push(middleNodeId);
          });
        });

        Object.keys(middleToTargets).forEach((targetNodeId) => {
          const middleNodeIds = Array.from(new Set(middleToTargets[targetNodeId]));
          if (middleNodeIds.length < 2) {
            return;
          }
          const middleNodesByRow = middleNodeIds
            .map((nodeId) => ({
              nodeId,
              rowIndex: rowIndexMap[nodeId],
            }))
            .filter((item) => Number.isFinite(item.rowIndex))
            .sort((left, right) => left.rowIndex - right.rowIndex);
          if (middleNodesByRow.length < 2) {
            return;
          }

          const nearestMiddle = middleNodesByRow[0];
          const farthestMiddle = middleNodesByRow[middleNodesByRow.length - 1];
          if (farthestMiddle.rowIndex - nearestMiddle.rowIndex <= 1) {
            return;
          }
          const sourceDepth = nodeDepths?.[sourceNodeId];
          const targetDepth = nodeDepths?.[targetNodeId];
          const minDepth = Math.min(
            Number.isFinite(sourceDepth) ? sourceDepth : 0,
            Number.isFinite(targetDepth) ? targetDepth : Number.MAX_SAFE_INTEGER
          );
          const maxDepth = Math.max(
            Number.isFinite(sourceDepth) ? sourceDepth : 0,
            Number.isFinite(targetDepth) ? targetDepth : Number.MAX_SAFE_INTEGER
          );
          const hasOccupiedBetween = allNodeIds.some((nodeId) => {
            if (
              nodeId === sourceNodeId ||
              nodeId === targetNodeId ||
              nodeId === nearestMiddle.nodeId ||
              nodeId === farthestMiddle.nodeId
            ) {
              return false;
            }
            const nodeDepth = nodeDepths?.[nodeId];
            const inCorridor =
              Number.isFinite(nodeDepth) &&
              nodeDepth >= minDepth &&
              nodeDepth <= maxDepth;
            if (!inCorridor) {
              return false;
            }
            const rowIndex = rowIndexMap[nodeId];
            return (
              Number.isFinite(rowIndex) &&
              rowIndex > nearestMiddle.rowIndex &&
              rowIndex < farthestMiddle.rowIndex
            );
          });
          if (hasOccupiedBetween) {
            return;
          }

          rowIndexMap[farthestMiddle.nodeId] = nearestMiddle.rowIndex + 1;
        });
      });
    },
  },
};
</script>

<style scoped>
/* 流程图容器 */
.flow-container {
  padding: 8px;
  position: relative;
  height: 70vh;
  min-height: 520px;
  overflow: hidden;
}

.flow-toolbar {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 4;
  padding: 6px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(4px);
}

.flow-toolbar :deep(.el-button) {
  margin-left: 0;
  padding: 6px;
  border: none;
  background: transparent;
  box-shadow: none;
  color: #606266;
}

.flow-toolbar :deep(.el-button:hover),
.flow-toolbar :deep(.el-button:focus) {
  color: #1890ff;
}

.zoom-text {
  min-width: 48px;
  text-align: right;
  font-size: 12px;
  color: #595959;
}

.viewport-shell {
  position: relative;
  height: 100%;
  overflow: hidden;
  cursor: grab;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: linear-gradient(180deg, #fafcff 0%, #ffffff 100%);
}

.viewport-shell.panning {
  cursor: grabbing;
}

.viewport-content {
  position: absolute;
  top: 0;
  left: 0;
}

/* 树状布局包装器 */
.tree-wrapper {
  position: relative;
  min-height: 150px;
}

/* SVG 连线层 */
.connections-layer {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 0;
}

.labels-layer {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 3;
}

.start-points-layer {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 4;
}

.edge-label {
  font-size: 10px;
  fill: #595959;
  stroke: #ffffff;
  stroke-width: 4px;
  paint-order: stroke;
  stroke-linejoin: round;
}

.edge-label-reject {
  font-size: 9px;
}

/* 树节点样式 */
.tree-node {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 8px;
  z-index: 1;
  transition: box-shadow 0.3s, transform 0.2s;
  box-sizing: border-box;
}

.tree-node:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  transform: translateY(-2px);
}

/* 头像图标 */
.node-avatar {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}

.node-avatar img {
  width: 28px;
  height: 28px;
}

/* 节点状态样式 */
.node-completed {
  border: 2px solid #1890ff;
  background: linear-gradient(135deg, #e6f7ff 0%, #ffffff 100%);
}

.node-completed .node-name {
  color: #1890ff;
}

.node-pending {
  border: 2px solid #d9d9d9;
  background: linear-gradient(135deg, #fafafa 0%, #ffffff 100%);
}

.node-pending .node-name {
  color: #2c2c2c;
  font-weight: 800;
}

/* 节点信息 */
.node-info {
  text-align: center;
  width: 100%;
}

.node-name {
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

.node-action {
  font-size: 10px;
  color: #666666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}

/* 无数据提示 */
.no-data {
  text-align: center;
  color: #999999;
  padding: 30px 0;
  font-size: 14px;
}

/* 响应式处理 */
@media screen and (max-width: 768px) {
  .flow-container {
    padding: 6px;
    min-height: 420px;
  }

  .tree-node {
    padding: 6px;
  }

  .node-avatar {
    width: 24px;
    height: 24px;
  }

  .node-avatar img {
    width: 24px;
    height: 24px;
  }

  .node-name {
    font-size: 10px;
  }

  .node-action {
    font-size: 9px;
  }
}
</style>
